import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { INDIAN_LOCATIONS } from '../../constants/recuritment-constants/indian-locations.constants';

export class EducatorValidators {

	// 1. Educator / Full Name Validator
	// Mandatory, Required, minimum 2 characters, maximum 100 characters, alphabets and spaces only.
	static educatorName(control: AbstractControl): ValidationErrors | null {
		const rawValue = control.value;
		if (rawValue === null || rawValue === undefined || rawValue === '') {
			return null;
		}
		const value = rawValue.trim();
		if (!value) {
			return { required: true };
		}

		if (value.length < 2) {
			return { minLengthError: true };
		}

		if (value.length > 100) {
			return { maxLengthError: true };
		}

		// Allow alphabets and spaces only
		const validCharsRegex = /^[A-Za-z\s]+$/;
		if (!validCharsRegex.test(value)) {
			return { invalidFormat: true };
		}

		return null;
	}

	// 2. Email Validator
	// Mandatory, Required, valid email format, unique email ID, maximum 255 characters.
	static email(control: AbstractControl): ValidationErrors | null {
		const rawValue = control.value;
		if (rawValue === null || rawValue === undefined || rawValue === '') {
			return null;
		}

		// Check for spaces anywhere in the email
		if (/\s/.test(rawValue)) {
			return { hasSpaces: true };
		}

		const value = rawValue.trim();
		if (value.length > 255) {
			return { maxLengthError: true };
		}

		// Standard email regex requiring a dot and a TLD suffix of at least 2 characters
		const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
		if (!emailRegex.test(value)) {
			return { invalidEmail: true };
		}

		return null;
	}

	// Email Uniqueness validator factory
	static uniqueEmail(existingEmails: string[]): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const rawValue = control.value;
			if (!rawValue) return null;
			const value = rawValue.trim().toLowerCase();
			if (existingEmails.map(e => e.trim().toLowerCase()).includes(value)) {
				return { emailExists: true };
			}
			return null;
		};
	}

	// 3. Mobile Validator
	// Mandatory, Required, 10-digit mobile number, numeric only, unique mobile number.
	static mobile(control: AbstractControl): ValidationErrors | null {
		const value = control.value;
		if (!value) return null;

		// Check if it is numbers only and exactly 10 digits
		const numericRegex = /^\d+$/;
		if (!numericRegex.test(value) || value.length !== 10) {
			return { invalidPhone: true };
		}

		return null;
	}

	// Mobile Uniqueness validator factory
	static uniqueMobile(existingMobiles: string[]): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (!value) return null;
			const trimmedValue = String(value).trim();
			if (existingMobiles.map(m => String(m).trim()).includes(trimmedValue)) {
				return { mobileExists: true };
			}
			return null;
		};
	}

	// 4. Subject Validator
	static subject(control: AbstractControl): ValidationErrors | null {
		const rawValue = control.value;
		if (rawValue === null || rawValue === undefined || rawValue === '') {
			return null;
		}
		const value = rawValue.trim();
		if (!value) return null;

		if (value.length < 2 || value.length > 150) {
			return { invalidLength: true };
		}

		// Allow alphabets, spaces, hyphen (-), ampersand (&), and parentheses ()
		const regex = /^[A-Za-z\s\-&()]+$/;
		if (!regex.test(value)) {
			return { invalidFormat: true };
		}

		return null;
	}

	// 5. Subject Specialization Validator
	// Mandatory, Required, minimum 2 characters, maximum 100 characters.
	static subjectSpecialization(control: AbstractControl): ValidationErrors | null {
		const rawValue = control.value;
		if (rawValue === null || rawValue === undefined || rawValue === '') {
			return null;
		}
		const value = rawValue.trim();
		if (!value) return null;

		if (value.length < 2 || value.length > 100) {
			return { invalidLength: true };
		}

		// Allow alphabets, spaces, and punctuation like -, &, (), commas, and slashes
		const regex = /^[A-Za-z\s\-&(),/]+$/;
		if (!regex.test(value)) {
			return { invalidFormat: true };
		}

		return null;
	}

	// 6. Years of Experience Validator
	// Optional, Numeric only, allow up to 2 decimal places, value between 0–50 years.
	static experienceYears(control: AbstractControl): ValidationErrors | null {
		const value = control.value;
		if (value === null || value === undefined || value === '') return null;

		const strValue = String(value).trim();
		// Allow positive numbers and decimals up to two decimal places (e.g., 25.55, 0.5, 2)
		const expRegex = /^\d+(\.\d{1,2})?$/;
		if (!expRegex.test(strValue)) {
			return { invalidExperience: true };
		}

		const num = Number(strValue);
		if (num < 0 || num > 50) {
			return { invalidExperience: true };
		}

		return null;
	}

	// 7. Current CTC Validator
	// Optional, Numeric only, allow up to 2 decimal places, value >= 0.
	static currentCtc(control: AbstractControl): ValidationErrors | null {
		const value = control.value;
		if (value === null || value === undefined || value === '') return null;

		const strValue = String(value).trim();
		const ctcRegex = /^\d+(\.\d{1,2})?$/;
		if (!ctcRegex.test(strValue)) {
			return { invalidCTC: true };
		}

		const num = Number(strValue);
		if (num < 0) {
			return { invalidCTC: true };
		}

		return null;
	}

	// 8. Expected CTC Validator
	// Optional, Numeric only, allow up to 2 decimal places, value >= 0.
	static expectedCtc(control: AbstractControl): ValidationErrors | null {
		const value = control.value;
		if (value === null || value === undefined || value === '') return null;

		const strValue = String(value).trim();
		const ctcRegex = /^\d+(\.\d{1,2})?$/;
		if (!ctcRegex.test(strValue)) {
			return { invalidCTC: true };
		}

		const num = Number(strValue);
		if (num < 0) {
			return { invalidCTC: true };
		}

		// Cross-field validation (Expected CTC >= Current CTC)
		const parent = control.parent;
		if (parent) {
			const currentCtcCtrl = parent.get('current_ctc');
			if (currentCtcCtrl && currentCtcCtrl.value !== null && currentCtcCtrl.value !== undefined && currentCtcCtrl.value !== '') {
				const currentCtcVal = Number(currentCtcCtrl.value);
				if (!isNaN(currentCtcVal) && num < currentCtcVal) {
					return { ctcLessThanCurrent: true };
				}
			}
		}

		return null;
	}

	// 9. Previous Institute Validator
	// Optional, Maximum 150 characters, alphanumeric and special characters allowed.
	static previousInstitute(control: AbstractControl): ValidationErrors | null {
		const rawValue = control.value;
		if (rawValue === null || rawValue === undefined || rawValue === '') {
			return null;
		}
		const value = rawValue.trim();
		if (!value) return null;

		if (value.length > 150) {
			return { invalidLength: true };
		}

		return null;
	}

	// 10. City Preference Validator
	// Optional, Valid city name, maximum 100 characters.
	static cityPreference(control: AbstractControl): ValidationErrors | null {
		const rawValue = control.value;
		if (rawValue === null || rawValue === undefined || rawValue === '') {
			return null;
		}
		const value = rawValue.trim();
		if (!value) return null;

		if (value.length > 100) {
			return { invalidLength: true };
		}

		// Valid city name from INDIAN_LOCATIONS constant
		const isValid = INDIAN_LOCATIONS.some(c => c.toLowerCase() === value.toLowerCase());
		if (!isValid) {
			return { invalidCity: true };
		}

		return null;
	}

	// 11. Cross-field Unique Cities Validator
	static uniqueCities(group: AbstractControl): ValidationErrors | null {
		const city1 = group.get('city_pref_1')?.value?.trim().toLowerCase();
		const city2 = group.get('city_pref_2')?.value?.trim().toLowerCase();
		const city3 = group.get('city_pref_3')?.value?.trim().toLowerCase();

		const cities = [city1, city2, city3].filter(c => !!c);
		const uniqueCities = new Set(cities);

		if (cities.length !== uniqueCities.size) {
			return { duplicateCities: true };
		}
		return null;
	}

	// 12. About Company Validator (Required, min 10, max 2000)
	static aboutCompany(control: AbstractControl): ValidationErrors | null {
		const value = control.value;
		if (value === null || value === undefined || value === '') {
			return { required: true };
		}
		const trimmedVal = String(value).trim();
		if (!trimmedVal) {
			return { required: true };
		}
		if (trimmedVal.length < 10) {
			return { minLengthError: true };
		}
		if (trimmedVal.length > 2000) {
			return { maxLengthError: true };
		}
		return null;
	}

	// 13. LinkedIn Company URL Validator (Optional)
	static linkedinCompanyUrl(control: AbstractControl): ValidationErrors | null {
		const value = control.value;
		if (value === null || value === undefined || value === '') {
			return null;
		}
		const trimmedVal = String(value).trim();
		if (!trimmedVal) return null;

		const linkedinPattern = /^(https?:\/\/)?(www\.)?linkedin\.com\/(company|in|school|showcase|pub|me)\/[a-zA-Z0-9_\-.]+\/?$/i;
		if (!linkedinPattern.test(trimmedVal)) {
			return { invalidLinkedinCompanyUrl: true };
		}
		return null;
	}

	// 14. HR Phone Validator (Optional, allows optional + prefix and country code, ends with valid 10-digit mobile)
	static hrPhone(control: AbstractControl): ValidationErrors | null {
		const value = control.value;
		if (value === null || value === undefined || value === '') {
			return null;
		}
		const trimmedVal = String(value).trim();
		if (!trimmedVal) return null;

		const onlyDigitsAndPlus = /^(\+)?\d+$/.test(trimmedVal);
		const validFormat = /^(\+\d+)?[6-9]\d{9}$/.test(trimmedVal);
		if (!onlyDigitsAndPlus || !validFormat) {
			return { invalidHrPhone: true };
		}
		return null;
	}
}